import { createApi } from "@reduxjs/toolkit/query/react";

import { Holiday, IBreak, TimeShift } from "@/interfaces/timeTracking";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

type settingType = "break" | "holiday";

export const timeTrackingApi = createApi({
  reducerPath: "timeTrackingApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["break", "shifts", "holiday"],
  endpoints: (builder) => ({
    // get the list of shifts like normal day,overnight,weekend etc
    getShifts: builder.query<TimeShift[], string>({
      query: (shiftCategory) =>
        `workspace/${getWorkspace()}/timetracking/settings/shift/${shiftCategory}`,
      providesTags: ["shifts"],
      transformResponse: (response: APIResponse<TimeShift[]>) => response?.data,
    }),

    // mutate the shift
    updateShift: builder.mutation<
      unknown,
      { data: TimeShift[]; shiftCategory: string }
    >({
      query: ({ data, shiftCategory }) => ({
        url: `workspace/${getWorkspace()}/timetracking/settings/shift/${shiftCategory}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["shifts"],
    }),

    // get the data by settingType eg: break,holiday
    getTimeSettingData: builder.query<
      GeneralResponse<TimeShift[]> | GeneralResponse<IBreak[]>,
      {
        type: settingType;
        filters: string;
      }
    >({
      query: ({ type, filters }) =>
        `workspace/${getWorkspace()}/timetracking/settings/${type}/${filters}`,
      providesTags: (_result, _error, { type }) => [type],
    }),

    // patch and post for the breaks and holiday
    mutateSettingType: builder.mutation<
      unknown,
      { data: unknown; settingType: settingType }
    >({
      query: ({ data, settingType }) => ({
        url: `workspace/${getWorkspace()}/timetracking/settings/${settingType}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { settingType }) => [settingType],
      transformResponse: (response: APIResponse<unknown>) => response?.data,
    }),

    // patch and post for the holiday settings
    mutateHolidaySettings: builder.mutation<unknown, unknown>({
      query: (data) => ({
        url: `workspace/${getWorkspace()}/timetracking/settings/holiday/settings`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["holiday"],
      transformResponse: (response: APIResponse<unknown>) => response?.data,
    }),

    // remove the breaks
    removeBreak: builder.mutation<IBreak, string>({
      query: (id) => ({
        url: `workspace/${getWorkspace()}/timetracking/settings/break/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["break"],
    }),

    getHolidays: builder.query<
      GeneralResponse<Holiday[]>,
      {
        filters: string;
      }
    >({
      query: ({ filters }) =>
        `workspace/${getWorkspace()}/timetracking/settings/holiday/${filters}`,
      providesTags: ["holiday"],
    }),

    getSingleHoliday: builder.query<unknown, string>({
      query: (holidayId) =>
        `workspace/${getWorkspace()}/timetracking/settings/holiday/${holidayId}`,
      providesTags: ["holiday"],
      transformResponse: (response: APIResponse<unknown>) => response?.data,
    }),

    // remove the holiday
    deleteHoliday: builder.mutation<object, string>({
      query: (id) => ({
        url: `workspace/${getWorkspace()}/timetracking/settings/holiday/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["holiday"],
    }),
  }),
});

export const {
  useGetShiftsQuery,
  useUpdateShiftMutation,
  useGetTimeSettingDataQuery,
  useMutateSettingTypeMutation,
  useRemoveBreakMutation,
  useMutateHolidaySettingsMutation,
  useDeleteHolidayMutation,
  useGetSingleHolidayQuery,
  useGetHolidaysQuery,
} = timeTrackingApi;
